import { useMemo } from "react"
import { Globe, Layers, Bug } from "lucide-react"
import SingleSelectCombobox, { type ComboboxOption } from "@/components/shared/SingleSelectCombobox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SpeciesLabel } from "@/components/shared/SpeciesLabel"
import type { CountryOption } from "./FilterList"
import VersionSelector from "./VersionSelector"
import { COLOR_COLUMN_OPTIONS } from "./MapLegend"

interface CountryFilterCardProps {
  countryOptions: CountryOption[]
  selectedCountries: string[]
  onCountriesChange: (countryIso3s: string[]) => void
  currentVersion: string
  selectableVersions: string[]
  onVersionChange: (version: string) => void
  selectedColumn: string
  onColumnChange: (column: string) => void
}

export default function CountryFilterCard({
  countryOptions,
  selectedCountries,
  onCountriesChange,
  currentVersion,
  selectableVersions,
  onVersionChange,
  selectedColumn,
  onColumnChange,
}: CountryFilterCardProps): React.JSX.Element {
  const comboboxOptions: ComboboxOption[] = useMemo(
    (): ComboboxOption[] =>
      countryOptions.map((country): ComboboxOption => ({ value: country.iso3, label: country.name })),
    [countryOptions],
  )
  const selectedValue: string = selectedCountries.length > 0 ? selectedCountries[0] : ""

  const selectedMetricLabel: string | undefined = COLOR_COLUMN_OPTIONS.find(
    (option) => option.value === selectedColumn,
  )?.label

  function handleChange(next: string): void {
    onCountriesChange(next === "" ? [] : [next])
  }

  return (
    <section
      aria-label="Dataset selectors"
      className="w-full overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md"
    >
      <div className="flex flex-col gap-2 px-3 py-2 min-[1440px]:flex-row min-[1440px]:items-center min-[1440px]:gap-3 min-[1440px]:py-[6px]">
        {/* Country */}
        <div className="flex w-full min-w-0 items-center gap-1.5 min-[1440px]:flex-1">
          <Globe className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-foreground/80 min-[1440px]:w-auto">
            Country
          </span>
          <div className="min-w-0 flex-1">
            <SingleSelectCombobox
              options={comboboxOptions}
              value={selectedValue}
              onChange={handleChange}
              placeholder="Search..."
              emptyMessage="No countries found."
              id="country-filter"
              className="h-6 w-full [&_[data-slot=input-group-control]]:text-[12px] [&_[data-slot=input-group-control]]:font-extrabold [&_[data-slot=input-group-control]]:placeholder:font-normal"
            />
          </div>
        </div>

        <span className="hidden h-5 w-px shrink-0 bg-border/60 min-[1440px]:inline-block" aria-hidden="true" />

        {/* Data version */}
        <div className="flex w-full items-center gap-1.5 min-[1440px]:w-auto min-[1440px]:shrink-0">
          <Layers className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-foreground/80 min-[1440px]:w-auto">
            Version
          </span>
          <div className="min-w-0 flex-1 min-[1440px]:flex-none">
            <VersionSelector
              currentVersion={currentVersion}
              versions={selectableVersions}
              onVersionChange={onVersionChange}
              side="bottom"
              triggerClassName="!h-6 w-full text-[11px] min-[1440px]:w-auto"
            />
          </div>
        </div>

        <span className="hidden h-5 w-px shrink-0 bg-border/60 min-[1440px]:inline-block" aria-hidden="true" />

        {/* Metric */}
        <div className="flex w-full min-w-0 items-center gap-1.5 min-[1440px]:flex-1">
          <Bug className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-foreground/80 min-[1440px]:w-auto">
            Metric
          </span>
          <Select value={selectedColumn} onValueChange={onColumnChange}>
            <SelectTrigger aria-label="Map metric" className="!h-6 w-full min-w-0 text-[11px]">
              <SelectValue placeholder="Select rate...">
                {selectedMetricLabel != null ? <SpeciesLabel label={selectedMetricLabel} /> : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent
              position="popper"
              side="bottom"
              align="start"
              sideOffset={4}
              className="max-h-48 min-w-48"
            >
              {COLOR_COLUMN_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <SpeciesLabel label={option.label} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </section>
  )
}
